import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { ClipboardPaste, UserPlus, Camera } from "lucide-react";

/**
 * Free-tier photo-OCR limit modal — fires when the session has used all
 * `limit` free scans. Two CTAs:
 *   1. "Create account"           → onCreateAccount?.()  (auth modal)
 *   2. "Paste ingredients instead" → onPasteInstead?.()  (paste-only flow)
 *
 * The paste flow is unlimited and is the unblocker for hitting users who
 * exhausted their photo budget.
 */
export default function PhotoOcrLimitModal({
  open,
  onOpenChange,
  used = 3,
  limit = 3,
  onCreateAccount,
  onPasteInstead,
  authEnabled = false,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="photo-ocr-limit-modal"
        className="bg-cream-50 border-surface-line rounded-3xl max-w-md p-7 sm:p-8"
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 text-amber-900 shrink-0">
            <Camera className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <DialogTitle
              data-testid="photo-ocr-limit-title"
              className="text-[22px] font-extrabold tracking-tight text-ink leading-tight"
            >
              You've used your {limit} free photo scans
            </DialogTitle>
            <DialogDescription
              data-testid="photo-ocr-limit-message"
              className="text-ink-soft/85 mt-2 text-[14px] leading-relaxed"
            >
              Create a free account to get more scans, or paste your
              ingredient list instead — pasting is unlimited and just as
              accurate.
            </DialogDescription>
          </div>
        </div>

        <div className="mt-2 inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.18em] font-bold text-ink-mute">
          <span
            data-testid="photo-ocr-limit-usage"
            className="inline-flex items-center px-2 h-5 rounded-full bg-amber-100 border border-amber-200 text-amber-900"
          >
            {used} / {limit} used
          </span>
          <span className="text-ink-light normal-case tracking-normal">
            Resets in 30 days
          </span>
        </div>

        <div className="mt-5 flex flex-col gap-2.5">
          <Button
            type="button"
            data-testid="photo-ocr-limit-create-account"
            onClick={() => onCreateAccount?.()}
            className="h-12 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-semibold"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create account
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="photo-ocr-limit-paste"
            onClick={() => onPasteInstead?.()}
            className="h-12 rounded-xl border-surface-line text-ink hover:bg-cream-100 text-[14px] font-semibold"
          >
            <ClipboardPaste className="w-4 h-4 mr-2" />
            Paste ingredients instead
          </Button>
        </div>

        {!authEnabled && (
          <p
            data-testid="photo-ocr-limit-auth-pending"
            className="mt-3 text-[11.5px] text-ink-mute italic"
          >
            Free accounts launch soon — until then, pasting is the fastest path.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
